import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CameraIcon, FileTextIcon, CheckCircleIcon, ClockIcon, ArrowRightIcon, StarIcon } from 'lucide-react';
const ServicesPage = () => {
  const [selectedService, setSelectedService] = useState<'photography' | 'valuation' | null>(null);
  const photographyPackages = [{
    id: 'basic',
    name: 'Basic Package',
    price: 199,
    features: ['Up to 15 professional photos', 'Interior and exterior shots', 'Basic editing and color correction', '24-hour delivery', 'High-resolution digital files'],
    popular: false
  }, {
    id: 'premium',
    name: 'Premium Package',
    price: 349,
    features: ['Up to 30 professional photos', 'Interior, exterior, and aerial drone shots', 'Advanced editing and HDR processing', 'Virtual twilight shots', '12-hour delivery', 'High-resolution digital files', 'Virtual tour creation'],
    popular: true
  }, {
    id: 'luxury',
    name: 'Luxury Package',
    price: 599,
    features: ['Unlimited professional photos', 'Full interior, exterior, and aerial coverage', 'Premium editing and retouching', 'Virtual twilight and day-to-dusk shots', '6-hour delivery', 'High-resolution digital files', '3D virtual tour with floor plans', 'Video walkthrough (2-3 minutes)', 'Dedicated photographer consultation'],
    popular: false
  }];
  const valuationServices = [{
    id: 'standard',
    name: 'Standard Valuation',
    price: 299,
    features: ['Certified property valuation report', 'Market analysis and comparables', 'Property condition assessment', 'Legal compliance verification', '3-5 business days delivery', 'Valid for 6 months'],
    turnaround: '3-5 business days'
  }, {
    id: 'express',
    name: 'Express Valuation',
    price: 449,
    features: ['Certified property valuation report', 'Comprehensive market analysis', 'Detailed property inspection', 'Legal compliance verification', 'Priority processing', '24-48 hours delivery', 'Valid for 6 months', 'One free revision'],
    turnaround: '24-48 hours'
  }];
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-1017">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-[rgb(var(--color-primary-dark))] py-16" data-id="element-1018">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1019">
          <div className="text-center" data-id="element-1020">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-1021">
              Professional Property Services
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto" data-id="element-1022">
              Enhance your listings with expert photography and certified
              property valuations
            </p>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16" data-id="element-1023">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1024">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-1025">
            {/* Photography Service */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden group hover:shadow-xl transition-shadow" data-id="element-1026">
              <div className="relative h-64 overflow-hidden" data-id="element-1027">
                <img src="https://images.unsplash.com/photo-1554995207-c18c203602cb?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" alt="Professional Photography" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-1028" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-1029"></div>
                <div className="absolute bottom-6 left-6" data-id="element-1030">
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-3" data-id="element-1031">
                    <CameraIcon className="h-7 w-7 text-primary" data-id="element-1032" />
                  </div>
                  <h2 className="text-2xl font-bold text-white" data-id="element-1033">
                    Professional Photography
                  </h2>
                </div>
              </div>
              <div className="p-8" data-id="element-1034">
                <p className="text-gray-600 mb-6" data-id="element-1035">
                  Showcase your property with stunning, high-quality photos that
                  attract more buyers and increase engagement by up to 80%.
                </p>
                <ul className="space-y-3 mb-6" data-id="element-1036">
                  <li className="flex items-start" data-id="element-1037">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-1038" />
                    <span className="text-sm text-gray-700" data-id="element-1039">
                      Professional equipment and lighting
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1040">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-1041" />
                    <span className="text-sm text-gray-700" data-id="element-1042">
                      Drone aerial photography available
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1043">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-1044" />
                    <span className="text-sm text-gray-700" data-id="element-1045">
                      Fast turnaround (6-24 hours)
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1046">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-1047" />
                    <span className="text-sm text-gray-700" data-id="element-1048">
                      Virtual tours and video walkthroughs
                    </span>
                  </li>
                </ul>
                <button onClick={() => setSelectedService('photography')} className="w-full btn-primary flex items-center justify-center group" data-id="element-1049">
                  View Packages
                  <ArrowRightIcon className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-1050" />
                </button>
              </div>
            </div>

            {/* Valuation Service */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden group hover:shadow-xl transition-shadow" data-id="element-1051">
              <div className="relative h-64 overflow-hidden" data-id="element-1052">
                <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" alt="Property Valuation" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-1053" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-1054"></div>
                <div className="absolute bottom-6 left-6" data-id="element-1055">
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-3" data-id="element-1056">
                    <FileTextIcon className="h-7 w-7 text-primary" data-id="element-1057" />
                  </div>
                  <h2 className="text-2xl font-bold text-white" data-id="element-1058">
                    Property Valuation
                  </h2>
                </div>
              </div>
              <div className="p-8" data-id="element-1059">
                <p className="text-gray-600 mb-6" data-id="element-1060">
                  Get certified property valuations from licensed professionals,
                  fully compliant with legal regulations and accepted by all
                  financial institutions.
                </p>
                <ul className="space-y-3 mb-6" data-id="element-1061">
                  <li className="flex items-start" data-id="element-1062">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-1063" />
                    <span className="text-sm text-gray-700" data-id="element-1064">
                      Certified by licensed appraisers
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1065">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-1066" />
                    <span className="text-sm text-gray-700" data-id="element-1067">
                      Legally compliant reports
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1068">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-1069" />
                    <span className="text-sm text-gray-700" data-id="element-1070">
                      Accepted by banks and lenders
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1071">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-1072" />
                    <span className="text-sm text-gray-700" data-id="element-1073">
                      Comprehensive market analysis
                    </span>
                  </li>
                </ul>
                <button onClick={() => setSelectedService('valuation')} className="w-full btn-primary flex items-center justify-center group" data-id="element-1074">
                  View Options
                  <ArrowRightIcon className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-1075" />
                </button>
              </div>
            </div>
          </div>

          {/* Photography Packages */}
          {selectedService === 'photography' && <div className="mb-16" data-id="element-1076">
              <div className="text-center mb-12" data-id="element-1077">
                <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1078">
                  Photography Packages
                </h2>
                <p className="text-lg text-gray-600" data-id="element-1079">
                  Choose the perfect package for your property
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1080">
                {photographyPackages.map(pkg => <div key={pkg.id} className={`relative bg-white rounded-2xl shadow-lg overflow-hidden ${pkg.popular ? 'ring-2 ring-primary transform scale-105' : ''}`} data-id="element-1081">
                    {pkg.popular && <div className="absolute top-0 right-0 bg-accent text-white text-xs font-bold px-4 py-1.5 rounded-bl-lg" data-id="element-1082">
                        Most Popular
                      </div>}
                    <div className="p-8" data-id="element-1083">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1084">
                        {pkg.name}
                      </h3>
                      <div className="mb-6" data-id="element-1085">
                        <span className="text-4xl font-bold text-primary" data-id="element-1086">
                          ${pkg.price}
                        </span>
                        <span className="text-gray-600 ml-2" data-id="element-1087">per session</span>
                      </div>
                      <ul className="space-y-3 mb-8" data-id="element-1088">
                        {pkg.features.map((feature, index) => <li key={index} className="flex items-start" data-id="element-1089">
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-1090" />
                            <span className="text-sm text-gray-700" data-id="element-1091">
                              {feature}
                            </span>
                          </li>)}
                      </ul>
                      <button className={`w-full ${pkg.popular ? 'btn-primary' : 'btn-secondary'}`} data-id="element-1092">
                        Book Now
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>}

          {/* Valuation Services */}
          {selectedService === 'valuation' && <div className="mb-16" data-id="element-1093">
              <div className="text-center mb-12" data-id="element-1094">
                <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1095">
                  Valuation Services
                </h2>
                <p className="text-lg text-gray-600" data-id="element-1096">
                  Certified property valuations by licensed professionals
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1097">
                {valuationServices.map(service => <div key={service.id} className="bg-white rounded-2xl shadow-lg p-8" data-id="element-1098">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1099">
                      {service.name}
                    </h3>
                    <div className="mb-4" data-id="element-1100">
                      <span className="text-4xl font-bold text-primary" data-id="element-1101">
                        ${service.price}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-6 bg-blue-50 px-3 py-2 rounded-lg w-fit" data-id="element-1102">
                      <ClockIcon className="h-4 w-4 mr-2 text-primary" data-id="element-1103" />
                      <span className="font-medium" data-id="element-1104">{service.turnaround}</span>
                    </div>
                    <ul className="space-y-3 mb-8" data-id="element-1105">
                      {service.features.map((feature, index) => <li key={index} className="flex items-start" data-id="element-1106">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-1107" />
                          <span className="text-sm text-gray-700" data-id="element-1108">
                            {feature}
                          </span>
                        </li>)}
                    </ul>
                    <button className="w-full btn-primary" data-id="element-1109">
                      Request Valuation
                    </button>
                  </div>)}
              </div>
            </div>}

          {/* Legal Compliance Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 max-w-4xl mx-auto" data-id="element-1110">
            <div className="flex items-start" data-id="element-1111">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1112">
                <CheckCircleIcon className="h-6 w-6 text-white" data-id="element-1113" />
              </div>
              <div className="ml-4" data-id="element-1114">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1115">
                  Legally Compliant Services
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-1116">
                  All our property valuation services are conducted by licensed
                  and certified appraisers in full compliance with local and
                  federal regulations. Our reports are accepted by:
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700" data-id="element-1117">
                  <li className="flex items-center" data-id="element-1118">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-1119" />
                    Banks and financial institutions
                  </li>
                  <li className="flex items-center" data-id="element-1120">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-1121" />
                    Government agencies
                  </li>
                  <li className="flex items-center" data-id="element-1122">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-1123" />
                    Legal proceedings
                  </li>
                  <li className="flex items-center" data-id="element-1124">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-1125" />
                    Tax authorities
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white" data-id="element-1126">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1127">
          <div className="text-center mb-12" data-id="element-1128">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1129">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1130">
              Trusted by property owners across the country
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1131">
            {[{
            name: 'Sarah Mitchell',
            role: 'Property Owner',
            text: 'The professional photography made a huge difference. My listing got 3x more views and sold within two weeks!',
            rating: 5
          }, {
            name: 'James Rodriguez',
            role: 'Real Estate Agent',
            text: 'The valuation report was comprehensive and accepted by the bank immediately. Saved us weeks of back-and-forth.',
            rating: 5
          }, {
            name: 'Emily Chen',
            role: 'Home Seller',
            text: 'Outstanding service! The photos were stunning and the whole process was seamless. Highly recommend.',
            rating: 5
          }].map((testimonial, index) => <div key={index} className="bg-gray-50 rounded-xl p-6" data-id="element-1132">
                <div className="flex items-center mb-4" data-id="element-1133">
                  {[...Array(testimonial.rating)].map((_, i) => <StarIcon key={i} className="h-5 w-5 text-yellow-500 fill-yellow-500" data-id="element-1134" />)}
                </div>
                <p className="text-gray-700 mb-4 italic" data-id="element-1135">
                  "{testimonial.text}"
                </p>
                <div data-id="element-1136">
                  <p className="font-semibold text-gray-900" data-id="element-1137">
                    {testimonial.name}
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-1138">{testimonial.role}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary to-[rgb(var(--color-primary-dark))]" data-id="element-1139">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1140">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1141">
            Ready to Enhance Your Listing?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1142">
            Book our professional services today and make your property stand
            out
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1143">
            <button onClick={() => setSelectedService('photography')} className="inline-block bg-white text-primary hover:bg-gray-50 font-bold px-8 py-4 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl" data-id="element-1144">
              Book Photography
            </button>
            <button onClick={() => setSelectedService('valuation')} className="inline-block bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white/20 font-bold px-8 py-4 rounded-lg transition-all duration-200" data-id="element-1145">
              Request Valuation
            </button>
          </div>
        </div>
      </section>
    </div>;
};
export default ServicesPage;